import { useEffect, useMemo, useState } from "react";

import qs from "query-string";
import { useDispatch, useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";

import { tableActionsCreator } from "app/actions/tableActions";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { SegmentSelection } from "app/components/React/TableSelectionComponents/segment-selection/SegmentSelection";
import { useEnrichedDomainData } from "app/custom-hooks/segments/useEnrichedDomainData";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { WithSegmentsLoading } from "app/pages/industry-analysis/frame/WithSegmentsLoading";
import { generateUniqueName } from "app/pages/segments/redesigned-wizard/segment-wizard/utils/validateSegmentName";
import { resolveSelectableState } from "app/pages/segments/utils/resolveSelectableState";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import DurationService from "app/services/DurationService";

import { FoldersTableColumns } from "./FoldersTableConfig";
import { FoldersTableTop } from "./FoldersTableTop";
import { TableWrapper, StyledTableTitle } from "./styledComponents";

interface IFoldersTableProps {
    params: Record<string, any>;
    folderKey: string;
    isCompare: boolean;
    keysArr: string[];
}

const TABLE_SELECTION_KEY = "WebsiteFoldersTable";
const TABLE_SELECTION_ATTRIBUTE = "Folder";
const DEFAULT_SORTED_COLUMN = "Share";
const DEFAULT_SORT_DIRECTION = "desc";
const ITEMS_PER_PAGE = 100;
const MAX_SELECTED_ROWS = ITEMS_PER_PAGE;
const SERVER_API = "widgetApi/LeadingFolders/LeadingFolders/Table";

const FoldersTable = (props: IFoldersTableProps) => {
    const { folderKey, params, keysArr, isCompare } = props;
    const [currentDomain, setCurrentDomain] = useState<string>(keysArr[0]);
    const enrichedDomainsData = useEnrichedDomainData([currentDomain]);

    const dispatch = useDispatch();
    const clearSelection = () => {
        dispatch(tableActionsCreator(TABLE_SELECTION_KEY, TABLE_SELECTION_ATTRIBUTE).clearAllSelectedRows());
    };
    useEffect(clearSelection, [currentDomain]);
    const durationApi = useMemo(() => {
        return DurationService.getDurationData(params.duration, params.comparedDuration);
    }, [DurationService]);
    const { isLoading, domainsValidForSegments = [] } = enrichedDomainsData;
    const selectableState = resolveSelectableState(domainsValidForSegments[0]);
    const { webSource, country } = params;
    const filters = {
        country,
        from: durationApi.forAPI.from,
        to: durationApi.forAPI.to,
        includeSubDomains: false,
        isWindow: durationApi?.forAPI?.isWindow,
        keys: currentDomain,
        webSource,
        orderBy: `${DEFAULT_SORTED_COLUMN} ${DEFAULT_SORT_DIRECTION}`,
        timeGranularity: "Monthly",
    };

    const excelUrl = useMemo(() => {
        return `widgetApi/LeadingFolders/LeadingFolders/Excel?${qs.stringify(filters)}`;
    }, [qs]);

    const { segmentsLoading, selectedRows } = useSelector((state) => {
        const {
            segmentsModule: { segmentsLoading, customSegmentsMeta },
        } = state;
        const selectedRowsFromTable = getTableSelectedItems(TABLE_SELECTION_KEY)(state);

        return { segmentsLoading, customSegmentsMeta, selectedRows: selectedRowsFromTable };
    });
    const { selectable, reasonKey } = selectableState;
    const selectionStatus = { selectable, tooltipKey: reasonKey };
    const selectionAmount = selectedRows.length;
    const textKey = translate(
        selectionAmount === 1 ? "folders.analysis.table.segment.selected" : "folders.analysis.table.segments.selected",
    );
    if (isLoading) {
        return null;
    }
    const defaultAssetName = segmentsLoading ? null : generateUniqueName(`${currentDomain} segment`);
    const getRules = (selectedRows) => {
        const folders = selectedRows.map(({ Folder: folder }) => folder);
        const rules = [{ words: [], folders, type: 0 }];
        return rules;
    };

    const tableTitle: string | undefined = proNavigator.current().viewConfig?.title;

    return (
        <TableWrapper>
            {tableTitle ? (
                <StyledTableTitle
                    fontVariant="h5"
                    color={colorsPalettes.carbon[500]}
                >
                    {i18nFilter()(tableTitle)}
                </StyledTableTitle>
            ) : null}
            <SWReactTableWrapperWithSelection
                tableSelectionKey={TABLE_SELECTION_KEY}
                tableSelectionProperty={TABLE_SELECTION_ATTRIBUTE}
                maxSelectedRows={MAX_SELECTED_ROWS}
                cleanOnUnMount={true}
                serverApi={SERVER_API}
                tableColumns={FoldersTableColumns.getColumns(selectionStatus)}
                initialFilters={filters}
                pageIndent={1}
                rowsPerPage={ITEMS_PER_PAGE}
                recordsField="Data"
                totalRecordsField="TotalCount"
                tableOptions={{
                    metric: "MarketingWorkspaceWebsiteGroupTable",
                    aboveHeaderComponents: proNavigator.isSalesSolution()
                        ? []
                        : [
                              <SegmentSelection
                                  key={TABLE_SELECTION_KEY}
                                  selectedRows={selectedRows}
                                  textKey={textKey}
                                  isLoading={segmentsLoading}
                                  domain={currentDomain}
                                  defaultAssetName={defaultAssetName}
                                  clearSelection={clearSelection}
                                  getRules={getRules}
                              />,
                          ],
                    tableSelectionTrackingParam: "Domain",
                    trackName: "Traffic Sources",
                }}
            >
                {(topComponentProps) => {
                    return (
                        <FoldersTableTop
                            {...topComponentProps}
                            isLoadingData={false}
                            excelLink={excelUrl}
                            initialFilters={filters}
                            downloadExcelPermitted={true}
                            keysArr={keysArr}
                            isCompare={isCompare}
                            currentDomain={currentDomain}
                            setCurrentDomain={setCurrentDomain}
                        />
                    );
                }}
            </SWReactTableWrapperWithSelection>
        </TableWrapper>
    );
};
const FoldersTableWithSegmentsLoading = WithSegmentsLoading(FoldersTable);
export default FoldersTableWithSegmentsLoading;
